/**
 * ObjectPool
 * 
 * Generic object pooling for Phaser game objects to reduce GC pressure.
 * Pre-allocates objects and reuses them instead of creating/destroying.
 */

import Phaser from 'phaser';

export interface PoolableRectangle extends Phaser.GameObjects.Rectangle {
  poolActive: boolean;
}

/**
 * Pool for Phaser Rectangle objects (used for effects, particles, etc.)
 */
export class RectanglePool {
  private scene: Phaser.Scene;
  private pool: PoolableRectangle[] = [];
  private active: Set<PoolableRectangle> = new Set();
  private initialSize: number;
  private maxSize: number;

  constructor(scene: Phaser.Scene, initialSize: number = 50, maxSize: number = 200) {
    this.scene = scene;
    this.initialSize = initialSize;
    this.maxSize = maxSize;
  }

  /**
   * Initialize the pool with pre-allocated objects
   */
  public init(): void {
    for (let i = 0; i < this.initialSize; i++) {
      const rect = this.createRectangle();
      this.pool.push(rect);
    }
    console.log(`🎱 RectanglePool initialized with ${this.initialSize} objects`);
  }

  /**
   * Create a new rectangle (internal use)
   */
  private createRectangle(): PoolableRectangle {
    const rect = this.scene.add.rectangle(0, 0, 1, 1, 0xffffff, 0) as PoolableRectangle;
    rect.poolActive = false;
    rect.setVisible(false);
    rect.setActive(false);
    return rect;
  }

  /**
   * Acquire a rectangle from the pool
   */
  public acquire(
    x: number,
    y: number,
    width: number,
    height: number,
    fillColor: number,
    fillAlpha: number = 1
  ): PoolableRectangle {
    let rect: PoolableRectangle;

    if (this.pool.length > 0) {
      rect = this.pool.pop()!;
    } else if (this.active.size < this.maxSize) {
      // Pool exhausted but under max, create new
      rect = this.createRectangle();
    } else {
      // At max capacity, reuse oldest active (fallback)
      console.warn('⚠️ RectanglePool at max capacity, reusing oldest');
      const firstActive = Array.from(this.active)[0];
      if (firstActive) {
        rect = firstActive;
        this.active.delete(rect);
      } else {
        // Absolute fallback - create new anyway
        rect = this.createRectangle();
      }
    }

    // Reset and configure
    rect.setPosition(x, y);
    rect.setSize(width, height);
    rect.setFillStyle(fillColor, fillAlpha);
    rect.setStrokeStyle(0);
    rect.setScale(1, 1);
    rect.setAlpha(1);
    rect.setDepth(0);
    rect.setVisible(true);
    rect.setActive(true);
    rect.poolActive = true;

    this.active.add(rect);
    return rect;
  }

  /**
   * Release a rectangle back to the pool
   */
  public release(rect: PoolableRectangle): void {
    if (!rect.poolActive) return;

    rect.poolActive = false;
    rect.setVisible(false);
    rect.setActive(false);
    this.active.delete(rect);
    this.pool.push(rect);
  }

  /**
   * Get pool statistics
   */
  public getStats(): { pooled: number; active: number; total: number } {
    return {
      pooled: this.pool.length,
      active: this.active.size,
      total: this.pool.length + this.active.size,
    };
  }

  /**
   * Clean up all objects (call on scene shutdown)
   */
  public destroy(): void {
    this.pool.forEach(rect => rect.destroy());
    this.active.forEach(rect => rect.destroy());
    this.pool = [];
    this.active.clear();
  }
}

/**
 * Helper to release a pooled rectangle after a tween completes
 */
export const releaseAfterTween = (
  scene: Phaser.Scene,
  pool: RectanglePool,
  rect: PoolableRectangle,
  tweenConfig: Omit<Phaser.Types.Tweens.TweenBuilderConfig, 'targets'>
): Phaser.Tweens.Tween => {
  const originalOnComplete = tweenConfig.onComplete;
  
  return scene.tweens.add({
    ...tweenConfig,
    targets: rect,
    onComplete: (tween, targets) => {
      if (originalOnComplete) {
        originalOnComplete(tween, targets);
      }
      pool.release(rect);
    },
  });
};
